import { useMutation } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { StreamPostInput } from '@/__generated__/graphql.ts';
import { gql } from '@/__generated__/index.ts';
import { updateFeedCache } from '@/apollo-cache-utils/update-feed-cache.ts';
import { StreamPostCardFragment } from './stream-post-card-fragment.ts';

const ADD_STREAM_POST = gql(`
  mutation AddStreamPost($input: StreamPostInput!) {
    addStreamPost(input: $input) {
      ...StreamPostCardFragment
    }
  }
`);

export const useAddStreamPost = () => {
  const toast = useToast();

  const [addStreamPostMutation, result] = useMutation(ADD_STREAM_POST, {
    onError() {
      toast.error({
        title: 'Failed to make a post',
        subTitle: 'Please try again',
      });
    },
    onCompleted() {
      toast.info({
        title: 'Your post was sent',
      });
    },
    update(cache, { data }) {
      const newPost = data?.addStreamPost;
      if (newPost) {
        cache.modify({
          fields: {
            streamPosts(postsPage, { storeFieldName }) {
              const streamPostsArgs = JSON.parse(
                storeFieldName.split('streamPosts:')[1],
              );

              if (streamPostsArgs.id === newPost.stream.id) {
                const newPostRef = cache.writeFragment({
                  fragment: StreamPostCardFragment,
                  fragmentName: 'StreamPostCardFragment',
                  data: newPost,
                });

                return updateFeedCache(postsPage, newPostRef);
              }
              return postsPage;
            },
          },
        });
      }
    },
  });

  const addStreamPost = (input: StreamPostInput) => {
    return addStreamPostMutation({
      variables: {
        input,
      },
    });
  };

  return [addStreamPost, result] as const;
};
